/**
 * Contact Seller Form
 *
 * Send messages to boat sellers
 */

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { MessageCreateRequestSchema } from "@/lib/api";
import { useCreateMessage } from "@/lib/api/hooks";
import { useUiStore } from "@/lib/store";
import { useAuthStore } from "@/lib/store";

interface Props {
  listingId: string;
}

export function ContactForm({ listingId }: Props) {
  const account = useAuthStore((s) => s.account);
  const navigate = useNavigate();
  const addNotification = useUiStore((s) => s.addNotification);
  const { mutate: sendMessage, isPending } = useCreateMessage();

  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: zodResolver(MessageCreateRequestSchema),
    defaultValues: {
      listingId,
      body: "",
    },
  });

  const onSubmit = (data: any) => {
    if (!account) {
      addNotification("Please sign in to contact sellers", "info");
      navigate("/auth/login");
      return;
    }

    sendMessage(data, {
      onSuccess: () => {
        addNotification("Message sent! Check your dashboard for responses.", "success");
        reset();
      },
      onError: (error) => {
        addNotification(error.message, "error");
      },
    });
  };

  if (!account) {
    return (
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-700 mb-3">Sign in to contact this seller</p>
        <button
          onClick={() => navigate("/auth/login")}
          className="w-full px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark transition text-sm font-semibold"
        >
          Sign In
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label htmlFor="body" className="block text-sm font-medium text-slate-700 mb-2">
          Your Message
        </label>
        <textarea
          {...register("body")}
          placeholder="Ask about the boat, schedule a viewing, make an offer..."
          rows={4}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm ${
            errors.body ? "border-red-500" : "border-ash"
          }`}
        />
        {errors.body && (
          <p className="text-red-500 text-xs mt-1">{errors.body.message}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isPending}
        className="w-full px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark disabled:opacity-50 transition font-semibold text-sm"
      >
        {isPending ? "Sending..." : "Send Message"}
      </button>
    </form>
  );
}
